import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { useUser } from '~/features/user/hooks/use-user';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import { getTranslatedLocaleConfig } from '../utils/catalog-locale';
import { updateCatalogUrlState } from '../utils/catalog-url-state-utils';

/**
 * Returns `true` when the current `?language=` query param is stale relative
 * to the user's settled language and should be cleared.
 *
 * Exported for unit testing — callers should use `useCatalogFilterLanguageQueryParam`
 * which handles the React lifecycle.
 */
export function isLanguageParamStale(param: string | undefined, settledLanguageCode: string): boolean {
  // No param or "all" are both intentional states — never stale.
  if (!param || param === 'all') {
    return false;
  }

  // Stale if the settled language has no maintained translation (English user)
  // or if the param doesn't match that language's filter code.
  const expectedFilterValue = getTranslatedLocaleConfig(settledLanguageCode)?.maintainedTranslationCode;
  return !expectedFilterValue || param !== expectedFilterValue;
}

export function useCatalogFilterLanguageQueryParam() {
  const router = useRouter();
  const { i18n } = useI18n();
  const isClient = useClientRender();
  const { isUserAuthenticated, userPreferences, userPreferencesIsFetched } = useUser();

  // Mirror WithInternationalization: the account's preferred language is the
  // source of truth and overrides the i18n detector default. Until preferences
  // settle, i18n.language can still be the detector value (often English) for a
  // maintained-translation account, so clearing then would wrongly strip a
  // valid ?language= param from a shared or bookmarked URL before i18n catches
  // up. Wait for the preferences fetch (authenticated) or the client mount
  // (anonymous) before deciding whether the param is stale.
  const preferenceSettled = isClient && (!isUserAuthenticated || userPreferencesIsFetched);
  const settledLanguage = userPreferences?.preferredLanguage ?? i18n.language;

  useEffect(() => {
    if (!preferenceSettled) {
      return;
    }

    // Clear language filter
    function removeLanguageQueryParam() {
      updateCatalogUrlState({
        router,
        updatedRouteParameters: {
          language: undefined,
        },
      });
    }

    // The language filter only applies to the user's own translated language.
    // Clear the param whenever it doesn't match the current language's
    // maintained-translation code (e.g. after switching languages).
    function clearIfStale(languageCode: string) {
      const currentParam = router.query.language;
      const param = Array.isArray(currentParam) ? currentParam[0] : currentParam;
      if (isLanguageParamStale(param, languageCode)) {
        removeLanguageQueryParam();
      }
    }

    clearIfStale(settledLanguage);

    function handleLanguageChange(incomingLanguage: string) {
      clearIfStale(incomingLanguage);
    }

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [router, i18n, preferenceSettled, settledLanguage]);
}
